/**
 * Closeout "who has the ball" — the pieces the approved mockup added
 * (https://claude.ai/artifact/6yPpNbU7So9a27GDhpwojF, "NEW" outlines), all
 * behind the per-person "Try who has the ball" switch (lib/closeout-ball.ts).
 *
 *   BallSwitch      — the switch itself.
 *   BallRail        — the rail split into "Yours" and "Waiting on someone",
 *                     one extra line per card (holder + days, amber dot > 2).
 *   BallChip        — first chip of NEEDED TO FINISH.
 *   QuoteTrail      — "Where the quote is" on the Estimate step.
 *   NeedsNewQuote   — sends an approved quote back to the approver. No task.
 *   JobNoteStar / NoteLine / JobNotesDialog — notes the office shares (10/16).
 *   WhyChip / WhyStrip — "Why isn't this out yet?" in one line.
 *
 * Visual first: colour and position carry the meaning; words are short.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    ArrowLeftToLine,
    ArrowRightToLine,
    ArrowUp,
    CircleAlert,
    CircleHelp,
    History,
    Lock,
    Star,
    Users,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { orgTodayKey } from "@/lib/datetime";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
    BALL_AMBER,
    BALL_COLOR,
    HANDOFF_COLOR,
    HANDOFF_REPLY_COLOR,
    daysLabel,
    handoffPointer,
    handoffReplyLine,
    handoffWaitLabel,
    noteDate,
    noteLine,
    whyNotOut,
    whyNotOutText,
    type Ball,
    type JobNote,
} from "@/lib/closeout-ball";
import type { QueueJob } from "./queue-list";
import { refreshCloseOutQueue } from "./queueRefresh";
import { HowItWorks } from "@/components/how-it-works";

// ─── The switch ─────────────────────────────────────────────────────────
export function BallSwitch({
    on,
    onChange,
    dark,
    forEveryone = false,
}: {
    on: boolean;
    onChange: (on: boolean) => void;
    dark?: boolean;
    /** The org turned it on for everyone: the switch is an opt-out. */
    forEveryone?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={() => onChange(!on)}
            title={
                on
                    ? "Back to the old Closeout. Only your view changes."
                    : "Every job says who has it, and your list splits into Yours and Waiting on someone. Only your view changes."
            }
            aria-pressed={on}
            className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold",
                dark
                    ? "border-white/25 text-shell-foreground/90 hover:bg-white/10"
                    : "border-border text-foreground/75 hover:bg-muted",
            )}
            data-testid="ball-switch"
        >
            <span
                className="h-2 w-2 rounded-full"
                style={{
                    background: on ? BALL_COLOR.office : "transparent",
                    border: `1.5px solid ${BALL_COLOR.office}`,
                }}
            />
            {forEveryone
                ? on
                    ? "Who has the ball: on · turn off for me"
                    : "Turn who has the ball back on"
                : on
                  ? "Who has the ball: on"
                  : "Try who has the ball"}
        </button>
    );
}

// ─── One line under a card ──────────────────────────────────────────────
/** Colour of a ball: pink while handed to someone, green when the answer
 *  came back, else the holder's colour. */
export function ballColor(ball: Ball): string {
    if (ball.handoff) return ball.handoff.state === "answered" ? HANDOFF_REPLY_COLOR : HANDOFF_COLOR;
    if (isStopped(ball)) return STOP_COLOR;
    return ball.mine ? BALL_COLOR.office : (BALL_COLOR[ball.kind] ?? BALL_COLOR.office);
}

/** Red for a hard stop: "Locked — COD: …" / "Held — no customer email on file". */
export const STOP_COLOR = "#b42318";
export function isStopped(ball: Ball): boolean {
    return /^(Locked|Held) — /.test(ball.text);
}

function BallMark({ ball, color }: { ball: Ball; color: string }) {
    if (ball.handoff?.state === "answered") return <ArrowLeftToLine className="h-3.5 w-3.5 flex-none" aria-hidden />;
    if (ball.handoff) return <ArrowRightToLine className="h-3.5 w-3.5 flex-none" aria-hidden />;
    if (/^Locked — /.test(ball.text)) return <Lock className="h-3.5 w-3.5 flex-none" aria-hidden />;
    if (/^Held — /.test(ball.text)) return <CircleAlert className="h-3.5 w-3.5 flex-none" aria-hidden />;
    return <span className="h-[7px] w-[7px] flex-none rounded-full" style={{ background: color }} />;
}

export function BallLine({ ball }: { ball: Ball }) {
    const color = ballColor(ball);
    return (
        <div
            className="mt-1 flex items-center gap-1.5 border-t border-dashed border-border pt-1 text-[11px] font-bold"
            style={{ color }}
            data-testid="ball-line"
            title={ball.handoff ? ball.text : undefined}
        >
            <BallMark ball={ball} color={color} />
            {ball.unseen && !ball.handoff && (
                <span
                    className="flex-none rounded px-1 text-[9.5px] font-extrabold uppercase tracking-wide text-white"
                    style={{ background: BALL_COLOR.city }}
                    title="New — not looked at yet"
                    data-testid="ball-new"
                >
                    New
                </span>
            )}
            <span className="min-w-0 truncate">{ball.text}</span>
            <span
                className={cn("ml-auto flex flex-none items-center gap-1", ball.stale ? "" : "text-foreground/50")}
                style={ball.stale ? { color: "#b45309" } : undefined}
            >
                {ball.stale && (
                    <span
                        className="h-[7px] w-[7px] rounded-full"
                        style={{ background: BALL_AMBER }}
                        aria-label="more than 2 work days"
                    />
                )}
                {daysLabel(ball.workDays)}
            </span>
        </div>
    );
}

// ─── "↑ Darcy's ask" ────────────────────────────────────────────────────
/** 11/4: while the big hand-off card is on the job, the other strips only
 *  point up at it, so the ask lives in one place. */
export function HandoffPointer({ handoff, onClick }: { handoff: NonNullable<Ball["handoff"]>; onClick?: () => void }) {
    const color = handoff.state === "answered" ? HANDOFF_REPLY_COLOR : HANDOFF_COLOR;
    const Tag = onClick ? "button" : "span";
    return (
        <Tag
            {...(onClick ? { type: "button" as const, onClick } : {})}
            className="inline-flex flex-none items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-extrabold"
            style={{ color, background: `${color}1f` }}
            title="See the big card at the top of the job"
            data-testid="handoff-pointer"
        >
            <ArrowUp className="h-3 w-3" aria-hidden />
            {handoffPointer(handoff)}
        </Tag>
    );
}

// ─── The queue card's hand-off top bar ─────────────────────────────────
/** Is this card's hand-off about the viewer? "to_me" = someone handed it to
 *  me and it is open; "back_to_me" = I handed it and the answer came back. */
export function handoffForMe(ball: Ball | null | undefined, me: string | null): "to_me" | "back_to_me" | null {
    const h = ball?.handoff;
    if (!h || !me) return null;
    if (h.state === "open" && h.toUserId === me) return "to_me";
    if (h.state === "answered" && h.fromUserId === me) return "back_to_me";
    return null;
}

/** 11/4: a card handed to you gets a solid pink top bar (green when the
 *  answer came back to you): the face, who, how long, and the ask. It
 *  replaces the card's thin ball line. The card's frame is set by the card. */
export function HandoffCardTop({ ball, me }: { ball: Ball; me: string | null }) {
    const which = handoffForMe(ball, me);
    const h = ball.handoff;
    if (!which || !h) return null;
    const toMe = which === "to_me";
    const color = toMe ? HANDOFF_COLOR : HANDOFF_REPLY_COLOR;
    const who = toMe ? h.fromName : h.replyByName || h.toName;
    const waited = toMe ? handoffWaitLabel(h.at) : "";
    return (
        <div className="-ml-4 -mr-3 -mt-2.5 mb-2" data-testid="handoff-card-top">
            <div
                className="flex items-center gap-2 rounded-t-[10px] px-3 py-1.5 text-[13px] font-extrabold text-white"
                style={{ background: color }}
            >
                <span
                    className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white text-[12px] font-black"
                    style={{ color }}
                    aria-hidden
                >
                    {(who || "?").charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0 truncate">
                    {toMe
                        ? `${who} handed this to you`
                        : `${who} ${h.outcome === "handed_back" ? "handed it back" : "answered you"}`}
                </span>
                {waited && (
                    <span className="ml-auto flex-none whitespace-nowrap rounded-full bg-white/25 px-2 py-0.5 text-[11px]">
                        {waited}
                    </span>
                )}
            </div>
            <div className="truncate px-4 pt-1.5 text-[12.5px] font-bold" style={{ color }}>
                {toMe
                    ? `“${h.reason}”`
                    : (h.reply ?? "").trim()
                      ? `“${handoffReplyLine(h).replace(/^[^:]*:\s*/, "")}”`
                      : (h.doneLine ?? handoffReplyLine(h))}
            </div>
        </div>
    );
}

// ─── The first chip in NEEDED TO FINISH ─────────────────────────────────
export function BallChip({ ball, onClick }: { ball: Ball; onClick?: () => void }) {
    if (ball.handoff) return <HandoffPointer handoff={ball.handoff} onClick={onClick} />;
    const color = ballColor(ball);
    return (
        <button
            type="button"
            onClick={onClick}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-0.5 text-left text-[10.5px] font-extrabold"
            style={{ color, background: `${color}1f` }}
            data-testid="ball-chip"
        >
            <BallMark ball={ball} color={color} />
            <span className="min-w-0 truncate">{ball.text}</span>
            {ball.workDays > 0 && (
                <span
                    className="inline-flex items-center gap-1"
                    style={ball.stale ? { color: "#b45309" } : { opacity: 0.7 }}
                >
                    {ball.stale && <span className="h-[7px] w-[7px] rounded-full" style={{ background: BALL_AMBER }} />}
                    {ball.workDays} {ball.workDays === 1 ? "day" : "days"}
                </span>
            )}
        </button>
    );
}

// ─── The rail ───────────────────────────────────────────────────────────
interface RailGroup {
    key: string;
    label: string;
    color: string;
    jobs: QueueJob[];
}

/** Split the queue for the rail: the viewer's jobs, then everyone else's
 *  grouped by who holds them. Jobs with no ball fall into "Yours" last (they
 *  are about to leave the queue). Exported for tests. */
export function splitForRail(jobs: QueueJob[]): { yours: QueueJob[]; waiting: RailGroup[] } {
    const yours: QueueJob[] = [];
    const groups = new Map<string, RailGroup>();
    const order: Record<string, number> = {
        handoff: 0,
        approver: 1,
        office: 2,
        city: 3,
        customer: 4,
        tech: 5,
        scheduling: 6,
    };
    for (const j of jobs) {
        const b = j.ball;
        if (!b || b.mine) {
            yours.push(j);
            continue;
        }
        const handed = !!b.handoff;
        const key = handed ? `handoff:${b.who}` : `${b.kind}:${b.who}`;
        let g = groups.get(key);
        if (!g) {
            const label = handed
                ? `Handed to ${b.who}`
                : b.kind === "city"
                  ? "City"
                  : b.kind === "customer"
                    ? "Customer"
                    : b.kind === "scheduling"
                      ? "Scheduling — return trip"
                      : b.who.charAt(0).toUpperCase() + b.who.slice(1);
            g = { key, label, color: handed ? HANDOFF_COLOR : (BALL_COLOR[b.kind] ?? BALL_COLOR.office), jobs: [] };
            groups.set(key, g);
        }
        g.jobs.push(j);
    }
    // 10/16: ready-to-act first (one tap finishes it), then jobs nobody has
    // opened since they landed here ("New — not looked at yet"), then
    // longest-held; jobs with no ball (about to leave the queue) last.
    // Hand to… (threads/hand-it-off): a job a person handed you, or an
    // answer that came back to you, leads the pile.
    yours.sort(
        (a, b) =>
            Number(!a.ball) - Number(!b.ball) ||
            Number(!a.ball?.handoff) - Number(!b.ball?.handoff) ||
            Number(!a.ball?.ready) - Number(!b.ball?.ready) ||
            Number(!a.ball?.unseen) - Number(!b.ball?.unseen) ||
            (b.ball?.workDays ?? 0) - (a.ball?.workDays ?? 0),
    );
    const waiting = [...groups.values()].sort(
        (a, b) => (order[a.key.split(":")[0]] ?? 9) - (order[b.key.split(":")[0]] ?? 9),
    );
    return { yours, waiting };
}

const YOURS_SHOWN = 7;
const PER_GROUP_SHOWN = 2;

export function BallRail({
    jobs,
    selectedId,
    renderCard,
    isApprover = false,
    onOpen,
}: {
    jobs: QueueJob[];
    selectedId: string;
    renderCard: (j: QueueJob) => React.ReactNode;
    /** The approver also does office work, so his Yours is big; he can narrow
     *  it to the jobs only he can move (10/16). */
    isApprover?: boolean;
    /** Opens a job (the catch-up strip's "Start here"). */
    onOpen?: (id: string) => void;
}) {
    const [justApprovals, setJustApprovals] = useState(false);
    const { yours: allYours, waiting } = splitForRail(jobs);
    const yours = justApprovals ? allYours.filter((j) => j.ball?.needsApprover) : allYours;
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    const waitingCount = waiting.reduce((n, g) => n + g.jobs.length, 0);
    // The open job always shows, even when it sits past the cut.
    const cut = (list: QueueJob[], n: number, open: boolean) =>
        open ? list : list.filter((j, i) => i < n || j.id === selectedId);
    return (
        <div data-testid="ball-rail">
            <CatchUpStrip jobs={allYours} onOpen={onOpen} />
            <RailHeading label="Yours" count={yours.length} />
            {isApprover && (
                <button
                    type="button"
                    onClick={() => setJustApprovals((v) => !v)}
                    aria-pressed={justApprovals}
                    className={cn(
                        "mb-1.5 rounded-full border px-2 py-0.5 text-[10.5px] font-bold",
                        justApprovals
                            ? "border-transparent bg-white text-[#5b21b6]"
                            : "border-white/25 text-shell-foreground/85 hover:bg-white/10",
                    )}
                    data-testid="just-my-approvals"
                >
                    <span
                        className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
                        style={{ background: BALL_COLOR.approver }}
                    />
                    Just my approvals · {allYours.filter((j) => j.ball?.needsApprover).length}
                </button>
            )}
            {cut(yours, YOURS_SHOWN, !!openGroups.__yours).map((j) => (
                <div key={j.id}>{renderCard(j)}</div>
            ))}
            {yours.length > YOURS_SHOWN && !openGroups.__yours && (
                <MoreButton
                    label={`+ ${yours.length - YOURS_SHOWN} more yours`}
                    onClick={() => setOpenGroups((o) => ({ ...o, __yours: true }))}
                />
            )}
            <div className="mt-3">
                <RailHeading label="Waiting on someone" count={waitingCount} />
            </div>
            {waiting.map((g) => {
                const open = !!openGroups[g.key];
                const shown = cut(g.jobs, PER_GROUP_SHOWN, open);
                return (
                    <div key={g.key} className="mb-1">
                        <div className="mb-1 mt-1.5 flex items-center gap-1.5 text-[10.5px] font-bold text-shell-foreground/70">
                            <span className="h-2 w-2 rounded-full" style={{ background: g.color }} />
                            {g.label} · {g.jobs.length}
                        </div>
                        {shown.map((j) => (
                            <div key={j.id}>{renderCard(j)}</div>
                        ))}
                        {g.jobs.length > shown.length && (
                            <MoreButton
                                label={`+ ${g.jobs.length - shown.length} more`}
                                onClick={() => setOpenGroups((o) => ({ ...o, [g.key]: true }))}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/** Work days after which a job counts as waited "over a week". */
export const CATCH_UP_WORK_DAYS = 5;

function todayKey(): string {
    return `closeout.catchup.dismissed.${orgTodayKey()}`;
}

/**
 * One-time "Catch up" strip (10/16): when some of your jobs have sat over a
 * week of work days, say how many and start you on the oldest. Got it hides it
 * for the day (this browser only). The only teaching item on the rail.
 */
export function CatchUpStrip({ jobs, onOpen }: { jobs: QueueJob[]; onOpen?: (id: string) => void }) {
    const [hidden, setHidden] = useState(() => {
        try {
            return localStorage.getItem(todayKey()) === "1";
        } catch {
            return false;
        }
    });
    const old = jobs
        .filter((j) => j.ball?.mine && (j.ball?.workDays ?? 0) > CATCH_UP_WORK_DAYS)
        .sort((a, b) => (b.ball?.workDays ?? 0) - (a.ball?.workDays ?? 0));
    if (hidden || old.length === 0) return null;
    const hide = () => {
        setHidden(true);
        try {
            localStorage.setItem(todayKey(), "1");
        } catch {
            /* private window: hides for this visit only */
        }
    };
    return (
        <div
            className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11.5px] text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/60 dark:text-amber-100"
            data-testid="catch-up-strip"
        >
            <div className="flex items-center gap-1.5 font-bold">
                <span className="h-2 w-2 flex-none rounded-full" style={{ background: BALL_AMBER }} />
                {old.length} job{old.length === 1 ? " has" : "s have"} waited over a week — start here
            </div>
            <div className="mt-1.5 flex gap-1.5">
                {onOpen && (
                    <button
                        type="button"
                        onClick={() => onOpen(old[0].id)}
                        className="rounded-md bg-amber-600 px-2 py-0.5 text-[11px] font-bold text-white hover:bg-amber-700"
                    >
                        Start with the oldest
                    </button>
                )}
                <button type="button" onClick={hide} className="px-1 text-[11px] font-semibold hover:underline">
                    Got it
                </button>
            </div>
        </div>
    );
}

function RailHeading({ label, count }: { label: string; count: number }) {
    return (
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[.06em] text-white">
            <span>{label}</span>
            <span className="rounded-full bg-white/15 px-1.5 text-[10px] font-bold">{count}</span>
        </div>
    );
}

function MoreButton({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="mb-1.5 px-0.5 text-[10.5px] font-bold text-shell-foreground/75 hover:text-shell-foreground"
        >
            {label}
        </button>
    );
}

// ─── Where the quote is (Estimate step) ─────────────────────────────────
export interface QuoteTrailStep {
    label: string;
    sub: string;
    state: "done" | "now" | "todo";
    mark?: string;
}

export function quoteTrail(input: {
    approvalState: "none" | "pending" | "approved" | "delta";
    status: string;
    approvedByName: string | null;
    approvedAt: string | null;
    ball: Ball | null;
    /** When the quote reached the customer (estimates.last_sent_at). */
    customerSentAt?: string | null;
    /** When a queued same-day send goes (quotes.send_on_approval). */
    autoSendAt?: string | null;
}): QuoteTrailStep[] {
    const day = (iso: string | null) =>
        iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
    const approved = input.approvalState === "approved";
    const sent = input.status === "sent" || input.status === "accepted";
    const accepted = input.status === "accepted";
    const nowWho = input.ball?.mine ? "You · now" : input.ball ? `${input.ball.who} · now` : "now";
    return [
        { label: "Written", sub: "Closeout", state: "done", mark: "✓" },
        {
            label: "Approved",
            sub: approved || sent ? [input.approvedByName, day(input.approvedAt)].filter(Boolean).join(" · ") : nowWho,
            state: approved || sent ? "done" : "now",
            mark: approved || sent ? "✓" : undefined,
        },
        {
            label: "Send it",
            sub: sent
                ? input.customerSentAt
                    ? `sent ${sendTime(input.customerSentAt)}`
                    : "sent"
                : approved && input.autoSendAt
                  ? `by itself · ${sendTime(input.autoSendAt)}`
                  : approved
                    ? nowWho
                    : "office",
            state: sent ? "done" : approved ? "now" : "todo",
            mark: sent ? "✓" : undefined,
        },
        {
            label: "Customer yes",
            sub: accepted ? "accepted" : sent ? "customer · now" : "customer",
            state: accepted ? "done" : sent ? "now" : "todo",
            mark: accepted ? "✓" : undefined,
        },
    ];
}

/** "Oct 15, 3:02 PM" in the office's time zone. */
export function sendTime(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Chicago",
    });
}

/** A queued same-day quote send (server: quoteAutoSendStatusForJob). */
export interface QuoteAutoSend {
    estimateId: string;
    status: "pending" | "sending" | "sent" | "held" | "skipped" | "failed" | string;
    sendAfter: string;
    sentAt: string | null;
    error: string | null;
}

const AUTO_SEND_WHY: Record<string, string> = {
    approval_delta: "it changed after it was approved. Approve it again.",
    approval_pending: "it is waiting on approval again.",
    no_recipient: "there is no customer email on file.",
    declined: "the customer declined it.",
    cancelled: "it was cancelled.",
    already_accepted: "the customer already said yes.",
    already_sent: "it was already sent.",
};

/**
 * Make It Professional #1 — where the quote's own email is, in one chip.
 * Green "Quote sent <time>", blue "Sends by itself by <time>", amber when the
 * same-day send stopped (and why). Nothing when the quote never went and
 * nothing is queued.
 */
export function QuoteSendChip({
    customerSentAt,
    autoSend,
}: {
    customerSentAt: string | null;
    autoSend: QuoteAutoSend | null;
}) {
    let tone: "sent" | "queued" | "stopped" | null = null;
    let text = "";
    if (customerSentAt) {
        tone = "sent";
        text = `Quote sent ${sendTime(customerSentAt)}`;
    } else if (autoSend && (autoSend.status === "pending" || autoSend.status === "sending")) {
        tone = "queued";
        text = `Sends by itself by ${sendTime(autoSend.sendAfter)}`;
    } else if (autoSend && ["held", "skipped", "failed"].includes(autoSend.status)) {
        tone = "stopped";
        text = `Didn't send by itself: ${AUTO_SEND_WHY[autoSend.error ?? ""] ?? "the email didn't go. Send it from the quote."}`;
    }
    if (!tone) return null;
    return (
        <div
            className={cn(
                "mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold",
                tone === "sent" && "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200",
                tone === "queued" && "bg-sky-100 text-sky-900 dark:bg-sky-950/50 dark:text-sky-200",
                tone === "stopped" && "bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200",
            )}
            data-testid="quote-send-chip"
        >
            <span
                className={cn(
                    "h-2 w-2 rounded-full",
                    tone === "sent" && "bg-emerald-600",
                    tone === "queued" && "bg-sky-600",
                    tone === "stopped" && "bg-amber-600",
                )}
            />
            {text}
        </div>
    );
}

export function QuoteTrail({ steps }: { steps: QuoteTrailStep[] }) {
    return (
        <div className="rounded-xl border border-border bg-card p-3" data-testid="quote-trail">
            <div className="mb-2 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-foreground/60">
                Where the quote is
            </div>
            <ol className="flex items-start">
                {steps.map((s, i) => (
                    <li key={s.label} className="relative flex flex-1 flex-col items-center gap-1 text-center">
                        {i > 0 && (
                            <span
                                className={cn(
                                    "absolute left-[-50%] top-[9px] h-[2px] w-full",
                                    s.state === "todo" ? "bg-border" : "bg-teal-700",
                                )}
                            />
                        )}
                        <span
                            className={cn(
                                "z-[1] flex h-[18px] w-[18px] items-center justify-center rounded-full border-2 text-[9px] font-extrabold",
                                s.state === "done" && "border-teal-700 bg-teal-700 text-white",
                                s.state === "now" &&
                                    "border-teal-700 bg-card ring-4 ring-teal-100 dark:ring-teal-900/50",
                                s.state === "todo" && "border-border bg-card",
                            )}
                        >
                            {s.mark ?? ""}
                        </span>
                        <span className="text-[11px] font-bold">{s.label}</span>
                        <span className="text-[10px] text-foreground/60">{s.sub}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

// ─── Needs a new quote ──────────────────────────────────────────────────
export function NeedsNewQuote({
    estimateId,
    jobId,
    approverName,
    onDone,
}: {
    estimateId: string;
    jobId: string;
    approverName: string;
    onDone: () => void;
}) {
    const qc = useQueryClient();
    const m = useMutation({
        mutationFn: () => api.post(`/api/close-out/estimates/${estimateId}/needs-new-quote`, {}),
        onSuccess: () => {
            toast.success(`Back with ${approverName} for a new quote.`);
            refreshCloseOutQueue(qc, { jobId });
            onDone();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't send it back. Try again."),
    });
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="needs-new-quote">
            <Button size="sm" variant="outline" disabled={m.isPending} onClick={() => m.mutate()}>
                ↺ Needs a new quote
            </Button>
            <span className="text-[11px] text-foreground/60">Sends it back to {approverName}. No task.</span>
        </div>
    );
}

// ─── Job notes: shared with the office (10/16) ──────────────────────────
// #1905 made stars private. Corbin, 10/16: wrong. Darcy's notes told the
// office WHY a job wasn't out yet. Every note shows to the office with the
// writer's name and date. A note never moves the ball (Hand to… does that).

/** The star at the head of a card / the Closeout header. Gold when the job
 *  has a note; hovering shows the newest ones with who wrote them. */
export function JobNoteStar({
    notes,
    onClick,
    className,
}: {
    notes: JobNote[] | null | undefined;
    onClick: () => void;
    className?: string;
}) {
    const list = notes ?? [];
    const has = list.length > 0;
    return (
        <span className={cn("group relative flex-none", className)}>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                }}
                aria-label={has ? "Job notes" : "Add a note for the office"}
                className="rounded p-0.5 hover:bg-black/5"
                data-testid="job-note-star"
                data-teach="closeout.notes-shared"
            >
                <Star
                    className="h-[17px] w-[17px]"
                    style={{ color: has ? "#e0a800" : "#b9b9ad", fill: has ? "#e0a800" : "transparent" }}
                />
            </button>
            {has && (
                <span className="pointer-events-none absolute left-[-6px] top-6 z-20 hidden w-[260px] rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-left text-[11.5px] font-normal text-foreground shadow-lg group-hover:block group-focus-within:block dark:border-amber-700/50 dark:bg-amber-950">
                    <span className="mb-1 flex items-center gap-1 text-[9.5px] font-extrabold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                        <Users className="h-3 w-3" aria-hidden /> Notes · the office sees these
                    </span>
                    {list.slice(0, 3).map((n) => (
                        <span key={n.id} className="block py-0.5">
                            {n.onlyMe && <Lock className="mr-1 inline h-3 w-3 align-[-2px]" aria-label="only you" />}
                            {noteLine(n)}
                        </span>
                    ))}
                    {list.length > 3 && <span className="block text-foreground/60">+ {list.length - 3} more</span>}
                </span>
            )}
        </span>
    );
}

/** One line on the card: "★ Darcy · Oct 14: waiting on PO from Cintas". */
export function NoteLine({ notes }: { notes: JobNote[] | null | undefined }) {
    const list = notes ?? [];
    const n = list[0];
    if (!n) return null;
    return (
        <div
            className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-amber-900 dark:text-amber-200"
            data-testid="note-line"
            title={list.map((x) => noteLine(x)).join("\n")}
        >
            <Star className="h-3 w-3 flex-none" style={{ color: "#e0a800", fill: "#e0a800" }} aria-hidden />
            {n.onlyMe && <Lock className="h-3 w-3 flex-none" aria-label="only you" />}
            <span className="min-w-0 truncate">{noteLine(n)}</span>
            {list.length > 1 && <span className="flex-none text-foreground/50">+{list.length - 1}</span>}
        </div>
    );
}

interface WhyJob {
    ball?: Ball | null;
    notes?: JobNote[] | null;
    packet_sent_at?: string | null;
}

function WhyBody({ why }: { why: NonNullable<ReturnType<typeof whyNotOut>> }) {
    return (
        <>
            {why.blocker && <span className="block font-extrabold">{why.blocker}</span>}
            {why.also && <span className="block text-foreground/70">{why.also}</span>}
            {why.note && (
                <span className="mt-0.5 flex items-start gap-1 text-amber-900 dark:text-amber-200">
                    <Star
                        className="mt-[2px] h-3 w-3 flex-none"
                        style={{ color: "#e0a800", fill: "#e0a800" }}
                        aria-hidden
                    />
                    <span>{why.note}</span>
                </span>
            )}
        </>
    );
}

/**
 * "Why isn't this out yet?" on a card: a small chip. Hover (desktop) or tap
 * (iPad / phone) shows the specific blocker and the newest shared note. The
 * tap never opens the job.
 */
export function WhyChip({ job }: { job: WhyJob }) {
    const [open, setOpen] = useState(false);
    const why = whyNotOut(job);
    if (!why) return null;
    return (
        <span className="group relative inline-flex flex-none" onMouseLeave={() => setOpen(false)}>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                aria-expanded={open}
                aria-label={`Why isn't this out yet? ${whyNotOutText(why)}`}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-1.5 py-px text-[10px] font-extrabold text-foreground/70 hover:bg-muted"
                data-testid="why-chip"
            >
                <CircleHelp className="h-3 w-3" aria-hidden /> Why?
            </button>
            <span
                role="tooltip"
                className={cn(
                    "absolute bottom-6 right-0 z-30 w-[250px] rounded-lg border border-border bg-card px-2.5 py-2 text-left text-[11.5px] font-normal leading-snug text-foreground shadow-lg",
                    open ? "block" : "hidden group-hover:block",
                )}
                data-testid="why-pop"
            >
                <span className="mb-0.5 block text-[9.5px] font-extrabold uppercase tracking-wide text-muted-foreground">
                    Why isn&apos;t this out yet?
                </span>
                <WhyBody why={why} />
            </span>
        </span>
    );
}

/** The same answer as one strip under the job name (Closeout header, job page). */
export function WhyStrip({ job, onNotes }: { job: WhyJob; onNotes?: () => void }) {
    const why = whyNotOut(job);
    if (!why) return null;
    return (
        <div
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-border bg-amber-50/60 px-3.5 py-1.5 text-[12px] dark:bg-amber-950/20"
            data-testid="why-strip"
        >
            <span className="flex flex-none items-center gap-1 text-[9.5px] font-extrabold uppercase tracking-wide text-muted-foreground">
                <CircleHelp className="h-3 w-3" aria-hidden /> Why isn&apos;t this out yet?
            </span>
            {job.ball?.handoff ? (
                <span className="self-center">
                    <HandoffPointer handoff={job.ball.handoff} />
                </span>
            ) : (
                why.blocker && <span className="font-extrabold">{why.blocker}</span>
            )}
            {why.also && <span className="text-foreground/70">· {why.also}</span>}
            {why.note && (
                <span className="inline-flex min-w-0 items-baseline gap-1 text-amber-900 dark:text-amber-200">
                    <Star
                        className="h-3 w-3 flex-none self-center"
                        style={{ color: "#e0a800", fill: "#e0a800" }}
                        aria-hidden
                    />
                    <span className="min-w-0">{why.note}</span>
                </span>
            )}
            {onNotes && (
                <button
                    type="button"
                    onClick={onNotes}
                    className="ml-auto text-[11px] font-bold text-foreground/60 underline-offset-2 hover:underline"
                >
                    {why.note ? "Notes" : "Add a note"}
                </button>
            )}
        </div>
    );
}

interface NotesResponse {
    notes: JobNote[];
    history: Array<{
        id: string;
        action: "wrote" | "edited" | "cleared" | "moved_in";
        text: string | null;
        onlyMe: boolean;
        byName: string;
        at: string;
    }>;
}

const HISTORY_WORD: Record<NotesResponse["history"][number]["action"], string> = {
    wrote: "wrote",
    edited: "changed",
    cleared: "cleared",
    moved_in: "note kept from before",
};

/** Notes on a job: everyone's, newest first; yours to write or change. */
export function JobNotesDialog({ jobId, jobName, onClose }: { jobId: string; jobName: string; onClose: () => void }) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const viewerId = user?.id ?? null;
    const q = useQuery({
        queryKey: ["close-out-notes", jobId],
        queryFn: () => api.get<NotesResponse>(`/api/close-out/jobs/${jobId}/notes`),
        staleTime: 0,
    });
    const notes = q.data?.notes ?? [];
    const mineNote = notes.find((n) => n.authorId === viewerId) ?? null;
    const [text, setText] = useState("");
    const [onlyMe, setOnlyMe] = useState(false);
    const [editing, setEditing] = useState<JobNote | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    useEffect(() => {
        setText(mineNote?.text ?? "");
        setOnlyMe(mineNote?.onlyMe ?? false);
    }, [mineNote?.id, mineNote?.text, mineNote?.onlyMe]);
    const done = (msg: string) => {
        toast.success(msg);
        qc.invalidateQueries({ queryKey: ["close-out-notes", jobId] });
        refreshCloseOutQueue(qc, { jobId });
    };
    const saveMine = useMutation({
        mutationFn: () => api.post(`/api/close-out/jobs/${jobId}/notes`, { text: text.trim(), onlyMe }),
        onSuccess: () => {
            done(!text.trim() ? "Note cleared." : onlyMe ? "Saved. Only you see it." : "Saved. The office sees it.");
            onClose();
        },
        onError: () => toast.error("Couldn't save the note. Try again."),
    });
    const saveOther = useMutation({
        mutationFn: (n: { id: string; text: string; onlyMe: boolean }) =>
            api.patch(`/api/close-out/notes/${n.id}`, { text: n.text, onlyMe: n.onlyMe }),
        onSuccess: () => {
            setEditing(null);
            done("Note changed.");
        },
        onError: () => toast.error("Couldn't change that note."),
    });
    const clear = useMutation({
        mutationFn: (id: string) => api.post(`/api/close-out/notes/${id}/clear`, {}),
        onSuccess: () => done("Note cleared. It stays in the history."),
        onError: () => toast.error("Couldn't clear that note."),
    });
    const others = notes.filter((n) => n.id !== mineNote?.id);
    return (
        <Dialog open onClose={onClose} title={`Notes · ${jobName}`}>
            <div className="space-y-3" data-testid="job-notes-dialog">
                <p className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground/70">
                    <Users className="h-3.5 w-3.5" aria-hidden /> The office sees these notes. A note doesn&apos;t hand
                    the job to anyone.
                </p>
                {others.length > 0 && (
                    <ul className="space-y-1.5" data-testid="others-notes">
                        {others.map((n) => (
                            <li
                                key={n.id}
                                className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12.5px] dark:border-amber-700/50 dark:bg-amber-950/40"
                            >
                                {editing?.id === n.id ? (
                                    <div className="space-y-1.5">
                                        <Textarea
                                            value={editing.text}
                                            onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                                            maxLength={2000}
                                            rows={3}
                                        />
                                        <div className="flex justify-end gap-2">
                                            <Button variant="outline" size="sm" onClick={() => setEditing(null)}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="sm"
                                                disabled={saveOther.isPending || !editing.text.trim()}
                                                onClick={() =>
                                                    saveOther.mutate({ id: n.id, text: editing.text, onlyMe: n.onlyMe })
                                                }
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-2">
                                        <Star
                                            className="mt-[3px] h-3.5 w-3.5 flex-none"
                                            style={{ color: "#e0a800", fill: "#e0a800" }}
                                            aria-hidden
                                        />
                                        <span className="min-w-0 flex-1">
                                            {noteLine(n)}
                                            {n.editedByName && (
                                                <span className="ml-1 text-[11px] text-foreground/55">
                                                    (changed by {n.editedByName})
                                                </span>
                                            )}
                                        </span>
                                        {n.canEdit && (
                                            <span className="flex flex-none gap-1">
                                                <button
                                                    type="button"
                                                    className="text-[11px] font-bold text-foreground/60 hover:underline"
                                                    onClick={() => setEditing(n)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-[11px] font-bold text-red-700 hover:underline dark:text-red-300"
                                                    disabled={clear.isPending}
                                                    onClick={() => clear.mutate(n.id)}
                                                >
                                                    Clear
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                <div className="space-y-1.5">
                    <label className="text-[12px] font-bold" htmlFor={`note-${jobId}`}>
                        {mineNote ? "Your note" : "Add your note"}
                    </label>
                    <Textarea
                        id={`note-${jobId}`}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        maxLength={2000}
                        rows={3}
                        placeholder="Why isn't it out yet? Example: waiting on PO from Cintas"
                        autoFocus
                    />
                    <label className="flex items-center gap-2 text-[12px] text-foreground/75">
                        <input
                            type="checkbox"
                            checked={onlyMe}
                            onChange={(e) => setOnlyMe(e.target.checked)}
                            data-testid="note-only-me"
                        />
                        <Lock className="h-3 w-3" aria-hidden /> Only me (the office won&apos;t see it)
                    </label>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex gap-2">
                        {mineNote && (
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled={clear.isPending}
                                onClick={() => {
                                    clear.mutate(mineNote.id);
                                    onClose();
                                }}
                            >
                                Clear my note
                            </Button>
                        )}
                        {(q.data?.history.length ?? 0) > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>
                                <History className="mr-1 h-3.5 w-3.5" aria-hidden />
                                History · {q.data!.history.length}
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            disabled={saveMine.isPending || (!text.trim() && !mineNote)}
                            onClick={() => saveMine.mutate()}
                            data-testid="note-save"
                        >
                            Save note
                        </Button>
                    </div>
                </div>
                {showHistory && (
                    <ol
                        className="max-h-48 space-y-1 overflow-y-auto border-t border-border pt-2 text-[11.5px]"
                        data-testid="note-history"
                    >
                        {q.data!.history.map((h) => (
                            <li key={h.id} className="text-foreground/75">
                                <b>{h.byName}</b> {HISTORY_WORD[h.action]} · {noteDate(h.at)}
                                {h.text ? `: ${h.text}` : ""}
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </Dialog>
    );
}

// ─── Documents: "Change to Couldn't test" ───────────────────────────────
// Brentwood Stair #102678621: a FAIL was saved, but the only "report" was a
// photo of a dirt-filled box — an access problem, not a test. The office turns
// it into "Couldn't test" with a reason. No return trip unless ticked.
export interface CantTestDevice {
    id: string;
    name: string | null;
    serial_number: string | null;
    disposition: string | null;
    last_test?: { overall_result?: string | null } | null;
}

// Couldn't test is only for "we couldn't get to it" (Corbin, 10/22). If the
// tech could reach the device, it is a fail, so those reasons aren't offered.
const CANT_TEST_CHOICES: Array<{ value: string; label: string }> = [
    { value: "no_access", label: "Locked / no key" },
    { value: "manager_unavailable", label: "Nobody to let us in" },
    { value: "unsafe", label: "Unsafe or blocked" },
];

export function ChangeToCouldNotTest({ jobId, devices }: { jobId: string; devices: CantTestDevice[] }) {
    const qc = useQueryClient();
    const tested = devices.filter(
        (d) => !d.disposition && (d.last_test?.overall_result === "pass" || d.last_test?.overall_result === "fail"),
    );
    const [assetId, setAssetId] = useState<string | null>(null);
    const [reason, setReason] = useState("");
    const [category, setCategory] = useState("no_access");
    const [returnTrip, setReturnTrip] = useState(false);
    const m = useMutation({
        mutationFn: () =>
            api.post(`/api/close-out/jobs/${jobId}/devices/${assetId}/could-not-test`, {
                reason: reason.trim(),
                category,
                needsReturnTrip: returnTrip,
            }),
        onSuccess: () => {
            toast.success(
                returnTrip ? "Changed to Couldn't test. Return trip is on the Grid." : "Changed to Couldn't test.",
            );
            setAssetId(null);
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["backflow", "job", jobId, "devices"] });
            qc.invalidateQueries({ queryKey: ["close-out-documents", jobId] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't change it. Try again."),
    });
    if (tested.length === 0) return null;
    const picked = tested.find((d) => d.id === assetId) ?? null;
    return (
        <>
            <div
                className="mb-2 flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[11.5px]"
                data-testid="change-to-cant-test"
            >
                <span className="text-foreground/60">Saved test really an access problem?</span>
                {tested.map((d) => (
                    <button
                        key={d.id}
                        type="button"
                        onClick={() => {
                            setAssetId(d.id);
                            setReason("");
                            setCategory("no_access");
                            setReturnTrip(false);
                        }}
                        className="rounded-full border border-border px-2 py-0.5 font-semibold hover:bg-muted"
                    >
                        Change to Couldn&apos;t test ·{" "}
                        {d.serial_number ? `SN ${d.serial_number}` : (d.name ?? "device")}
                    </button>
                ))}
            </div>
            {picked && (
                <Dialog open onClose={() => setAssetId(null)} title="Change to Couldn't test">
                    <div className="space-y-3 text-sm">
                        <p className="text-foreground/75">
                            {picked.name ?? "Device"}
                            {picked.serial_number ? ` · SN ${picked.serial_number}` : ""} has a saved{" "}
                            <b>{picked.last_test?.overall_result === "fail" ? "FAIL" : "PASS"}</b>. It will count as not
                            tested. The readings stay on the device, marked Couldn&apos;t test.
                        </p>
                        <div className="flex items-start gap-2">
                            <p className="flex-1 text-[12px] text-foreground/60">
                                Only when the tech couldn&apos;t get to the device. If he could reach it but
                                couldn&apos;t test it, it stays a fail.
                            </p>
                            <HowItWorks
                                title="How Change to Couldn't test works"
                                testId="how-closeout-cant-test"
                                steps={[
                                    {
                                        title: 'Only for "couldn\'t get to it"',
                                        text: "Use it when the saved test was really an access problem: locked, nobody to let us in, unsafe or blocked. If the tech could reach the device, leave it a fail.",
                                        picture: "closeout-cant-test.png",
                                        alt: "The Change to Couldn't test box with the reasons and the button ringed",
                                    },
                                    {
                                        title: "Pick the reason and write a short note",
                                        text: "The readings stay on the device. It stops counting as a test or a fail.",
                                    },
                                    {
                                        title: "Needs a return trip?",
                                        text: "Tick it to put the device on the Grid's return list. Leave it off if nobody goes back.",
                                    },
                                ]}
                            />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {CANT_TEST_CHOICES.map((c) => (
                                <button
                                    key={c.value}
                                    type="button"
                                    onClick={() => setCategory(c.value)}
                                    className={cn(
                                        "rounded-full border px-2.5 py-1 text-[12px] font-semibold",
                                        category === c.value
                                            ? "border-teal-700 bg-teal-700 text-white"
                                            : "border-border hover:bg-muted",
                                    )}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            maxLength={1000}
                            placeholder='Why? e.g. "Meter box full of dirt"'
                        />
                        <label className="flex items-center gap-2 text-[12.5px]">
                            <input
                                type="checkbox"
                                checked={returnTrip}
                                onChange={(e) => setReturnTrip(e.target.checked)}
                            />
                            Needs a return trip (puts it on the Grid)
                        </label>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setAssetId(null)}>
                                Cancel
                            </Button>
                            <Button size="sm" disabled={!reason.trim() || m.isPending} onClick={() => m.mutate()}>
                                Change to Couldn&apos;t test
                            </Button>
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    );
}

// ─── "No return — replacement quoted" (Mercantile Bldg 5) ───────────────
export function NoReturnReplacementQuoted({ jobId, onDone }: { jobId: string; onDone: () => void }) {
    const qc = useQueryClient();
    const m = useMutation({
        mutationFn: () => api.post<{ devices: number }>(`/api/close-out/jobs/${jobId}/no-return`, {}),
        onSuccess: () => {
            toast.success("No return trip. The replacement quote carries it.");
            refreshCloseOutQueue(qc, { jobId });
            qc.invalidateQueries({ queryKey: ["backflow", "job", jobId, "devices"] });
            onDone();
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't save that. Try again."),
    });
    return (
        <Button
            size="sm"
            variant="outline"
            className="mt-2.5 h-8 w-full text-xs sm:ml-2 sm:w-auto"
            disabled={m.isPending}
            onClick={() => m.mutate()}
            data-testid="no-return-replacement"
        >
            No return — replacement quoted
        </Button>
    );
}
